import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart3,
  Music,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Users,
  Coins,
  MapPin,
  Calendar,
  Sparkles,
  X,
  RefreshCw,
  AlertCircle,
  TrendingUp,
} from "lucide-react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function SessionStatsModal({
  open,
  onClose,
  venueId = null,
  initialData = null,
}) {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(initialData || null);
  const [error, setError] = useState(null);

  const fetchVenueStats = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_OVERVIEW(venueId));
      if (!res.ok) throw new Error(`Failed to load session stats: ${res.status}`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error("Fetch venue stats error:", err);
      setError(err.message || "Could not fetch venue statistics");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setStats(initialData);
      } else if (venueId) {
        fetchVenueStats();
      }
    }
  }, [open, venueId, initialData, fetchVenueStats]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Past Session";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const songsAccepted = stats?.totalSongsAccepted || 0;
  const songsRejected = stats?.totalSongsRejected || 0;
  const songsTotalProcessed = songsAccepted + songsRejected;
  const acceptanceRate =
    songsTotalProcessed > 0
      ? Math.round((songsAccepted / songsTotalProcessed) * 100)
      : 100;

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/65 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-3xl bg-white shadow-2xl border border-purple-100 flex flex-col"
        >
          {/* Hero Banner with Venue Photo & Name (Matches Android VenueStatsActivity) */}
          <div className="relative h-48 sm:h-56 bg-gradient-to-tr from-[#2D0A4A] to-[#601878] overflow-hidden shrink-0">
            {stats?.photoUrl && (
              <img
                src={stats.photoUrl}
                alt={stats.name || "Venue"}
                className="w-full h-full object-cover mix-blend-overlay opacity-60"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

            {/* Top action buttons */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1 rounded-full text-white text-xs font-semibold">
                <BarChart3 className="w-3.5 h-3.5 text-purple-300" />
                <span>Session Recap & Stats</span>
              </div>

              <div className="flex items-center gap-2">
                {venueId && (
                  <button
                    type="button"
                    onClick={fetchVenueStats}
                    title="Refresh stats"
                    className="p-2 bg-black/40 hover:bg-black/60 backdrop-blur-md text-white rounded-full transition-colors"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                    />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 bg-black/40 hover:bg-black/60 backdrop-blur-md text-white rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Venue & Event Info at Bottom of Banner */}
            <div className="absolute bottom-4 left-5 right-5 z-10 text-white space-y-1">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white drop-shadow-sm">
                {stats?.name || "Session Statistics"}
              </h2>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-purple-100 font-medium">
                {stats?.themes && stats.themes.length > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full bg-purple-500/40 backdrop-blur-xs text-white border border-purple-300/30">
                    Theme: {stats.themes.join(", ")}
                  </span>
                )}
                {stats?.startAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-purple-300" />
                    <span>{formatDate(stats.startAt)}</span>
                  </span>
                )}
                {stats?.address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-purple-300" />
                    <span>
                      {stats.address.city ||
                        stats.address.line1 ||
                        stats.address.formatted_address ||
                        "Venue Location"}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
            {loading ? (
              <div className="py-16 flex flex-col items-center justify-center space-y-3">
                <div className="w-8 h-8 border-3 border-[#72288D] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-medium text-gray-500">
                  Compiling session statistics...
                </p>
              </div>
            ) : error ? (
              <div className="rounded-2xl p-6 bg-red-50 text-center border border-red-100">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={fetchVenueStats}
                  className="mt-3 px-4 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors"
                >
                  Retry Loading
                </button>
              </div>
            ) : !stats ? (
              <div className="rounded-3xl p-10 text-center bg-gray-50 border border-dashed border-gray-200">
                <p className="text-sm text-gray-500 font-medium">
                  No statistical data found for this session.
                </p>
              </div>
            ) : (
              <>
                {/* Acceptance Rate Visual Progress Bar */}
                <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100/80 space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-gray-800">
                    <span className="flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4 text-[#72288D]" />
                      <span>Audience Song Acceptance Ratio</span>
                    </span>
                    <span className="text-sm font-extrabold text-[#72288D]">
                      {acceptanceRate}% Accepted
                    </span>
                  </div>
                  <div className="h-3 w-full bg-gray-200/80 rounded-full overflow-hidden flex">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${acceptanceRate}%` }}
                      transition={{ duration: 0.6 }}
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-l-full"
                    />
                    <div
                      style={{ width: `${100 - acceptanceRate}%` }}
                      className="h-full bg-rose-400 rounded-r-full"
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
                    <span className="text-emerald-700 font-bold">
                      {songsAccepted} Accepted
                    </span>
                    <span className="text-rose-600 font-bold">
                      {songsRejected} Rejected
                    </span>
                  </div>
                </div>

                {/* Key Metrics Grid (6 Metric Cards) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
                  {/* Songs Played */}
                  <div className="p-4 rounded-2xl bg-white border border-purple-100/80 shadow-xs hover:shadow-md transition-all">
                    <div className="w-8 h-8 rounded-xl bg-purple-100 text-[#72288D] flex items-center justify-center mb-2">
                      <Music className="w-4 h-4" />
                    </div>
                    <div className="text-2xl font-black text-gray-900">
                      {stats.totalSongsPlayed ?? 0}
                    </div>
                    <div className="text-xs font-semibold text-gray-500">
                      Songs Played
                    </div>
                  </div>

                  {/* Songs Accepted */}
                  <div className="p-4 rounded-2xl bg-white border border-emerald-100/80 shadow-xs hover:shadow-md transition-all">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-2">
                      <ThumbsUp className="w-4 h-4" />
                    </div>
                    <div className="text-2xl font-black text-emerald-700">
                      {stats.totalSongsAccepted ?? 0}
                    </div>
                    <div className="text-xs font-semibold text-gray-500">
                      Accepted Requests
                    </div>
                  </div>

                  {/* Songs Rejected */}
                  <div className="p-4 rounded-2xl bg-white border border-rose-100/80 shadow-xs hover:shadow-md transition-all">
                    <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center mb-2">
                      <ThumbsDown className="w-4 h-4" />
                    </div>
                    <div className="text-2xl font-black text-rose-600">
                      {stats.totalSongsRejected ?? 0}
                    </div>
                    <div className="text-xs font-semibold text-gray-500">
                      Rejected Requests
                    </div>
                  </div>

                  {/* Fast Track Requests */}
                  <div className="p-4 rounded-2xl bg-white border border-amber-100/80 shadow-xs hover:shadow-md transition-all">
                    <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center mb-2">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div className="text-2xl font-black text-amber-600">
                      {stats.totalSongsFastTrack ?? 0}
                    </div>
                    <div className="text-xs font-semibold text-gray-500">
                      Fast Track Boosts
                    </div>
                  </div>

                  {/* Unique Requesters */}
                  <div className="p-4 rounded-2xl bg-white border border-indigo-100/80 shadow-xs hover:shadow-md transition-all">
                    <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-2">
                      <Users className="w-4 h-4" />
                    </div>
                    <div className="text-2xl font-black text-indigo-700">
                      {stats.totalUniqueUsersRequested ?? stats.totalUniqueUsers ?? 0}
                    </div>
                    <div className="text-xs font-semibold text-gray-500">
                      Unique Requesters
                    </div>
                  </div>

                  {/* Coins Spent */}
                  <div className="p-4 rounded-2xl bg-white border border-purple-100/80 shadow-xs hover:shadow-md transition-all">
                    <div className="w-8 h-8 rounded-xl bg-purple-100 text-[#72288D] flex items-center justify-center mb-2">
                      <Coins className="w-4 h-4" />
                    </div>
                    <div className="text-2xl font-black text-[#72288D]">
                      {stats.totalCoinsSpent ?? 0}
                    </div>
                    <div className="text-xs font-semibold text-gray-500">
                      Audience Coins Spent
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#72288D]" />
              <span>Session overview data synced from venue performance telemetry</span>
            </span>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
